import React, { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { useGetBenefitsResetDate } from '@lyrahealth-inc/shared-app-logic/src/features/benefits/hooks/useGetBenefitsResetDate'

import { ClientDetailsSideBarSection, ClientDetailsSideBarSectionItem } from './ClientDetailsSideBarSection'
import { Display } from '../../atoms/display/Display'
import { Divider } from '../../atoms/divider/Divider'
import { DisplaySize } from '../../styles/typeStyles'
import { Details } from '../../ui-models/messages/Details'

const SectionWrapper = styled.View({
  flexDirection: 'column',
})

const StyledDivider = styled(Divider)(({ theme }) => ({
  paddingHorizontal: theme.spacing['24px'],
}))

const ContentWrapper = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['32px'],
  paddingHorizontal: theme.spacing['24px'],
  flexDirection: 'column',
  gap: theme.spacing['8px'],
}))

export type ClientDetailsBenefitsSideBarSectionProps = {
  details: Details
}

export const ClientDetailsBenefitsSideBarSection: React.FC<ClientDetailsBenefitsSideBarSectionProps> = ({
  details,
}) => {
  const { colors } = useTheme()
  const { employer: employerName, sessionsUsed, sessionsLimit, sessionCountStartsOn } = details.client

  const benefitsResetDate = useGetBenefitsResetDate(
    sessionCountStartsOn ? { session_count_starts_on: sessionCountStartsOn } : null,
  )

  const items: ClientDetailsSideBarSectionItem[] = useMemo(() => {
    const benefitItems: ClientDetailsSideBarSectionItem[] = []

    // EAP sessions used
    if (sessionsUsed !== undefined && sessionsLimit !== undefined) {
      benefitItems.push({
        key: 'eap-sessions-used',
        label: <FormattedMessage defaultMessage='EAP sessions used' description='Label for EAP sessions used' />,
        textValue: (
          <FormattedMessage
            defaultMessage='{sessionsUsed} of {sessionsLimit}'
            description='EAP sessions used value'
            values={{ sessionsUsed, sessionsLimit }}
          />
        ),
        testID: 'ClientDetailsBenefits-eap-sessions-used',
      })
    }

    // Benefits reset date
    if (benefitsResetDate) {
      benefitItems.push({
        key: 'benefits-reset',
        label: <FormattedMessage defaultMessage='Benefits reset' description='Label for benefits reset date' />,
        textValue: benefitsResetDate,
        testID: 'ClientDetailsBenefits-benefits-reset',
      })
    }

    return benefitItems
  }, [sessionsUsed, sessionsLimit, benefitsResetDate])

  if (!employerName) {
    return null
  }

  return (
    <SectionWrapper>
      <StyledDivider height={1} color={colors.borderDefault} />
      <ContentWrapper>
        <Display
          size={DisplaySize.EYEBROW}
          text={
            <FormattedMessage
              defaultMessage='Benefits sponsored by {employerName}'
              description='Header for benefits section with employer name'
              values={{ employerName }}
            />
          }
        />
        {items.length > 0 && <ClientDetailsSideBarSection items={items} />}
      </ContentWrapper>
    </SectionWrapper>
  )
}
